import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { trainingPrograms } from '../data/mock';
import {
  Clock,
  Award,
  BookOpen,
  CheckCircle,
  ArrowLeft,
  Wrench,
  TrendingUp,
  ShieldCheck,
  Briefcase,
  Users,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const ProgramDetailPage = ({ onRegister }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const program = trainingPrograms.find(p => p.slug === slug);

  /* Scroll to top on route change */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!program) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', paddingTop: '80px' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 className="display-large" style={{ marginBottom: '20px' }}>Program Not Found</h2>
          <Button className="btn-primary" onClick={() => navigate('/')}>Go Back Home</Button>
        </div>
      </div>
    );
  }

  // DYNAMIC SEO STRINGS
  const seoTitle = `Best ${program.title} Training in Indore | Ciqura Labs Certification`;
  const seoDescription = `Enroll in the ${program.duration} ${program.title} course at Ciqura Labs Indore. Professional curriculum with hands-on labs and guaranteed internship at Ciqur24 Bengaluru.`;
  const canonicalUrl = `https://ciquralabs.ciqur24.com/programs/${slug}`;
  const registrationLink = "https://forms.office.com/Pages/ResponsePage.aspx?id=GIlUwqulxkW2gUlR5VePC12nZ4fz5yRFkrQsWYzu1nBUN01NTlFFS0lQOFhRNjk4MDVaQloxTkJBRS4u";

  // CONSTRUCTING DYNAMIC KEYWORDS FOR 2026
  const dynamicKeywords = [
    `${program.title} training Indore`,
    `${program.title} course Vijay Nagar`,
    "Ciqura Labs Indore",
    "Ethical Hacking classes Indore",
    "Cybersecurity certification MP",
    "AI and Machine Learning Indore",
    "IT training Shagun Arcade",
    "Ciqur24 Bengaluru internship",
    ...program.skills.slice(0, 8)
  ].join(', ');

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
      
      {/* DYNAMIC SEO METADATA */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={dynamicKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph (Social Sharing) */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={program.image} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />

        {/* RICH SNIPPETS: COURSE & FAQ SCHEMA */}
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Course",
              "name": program.title,
              "description": program.description,
              "provider": {
                "@type": "EducationalOrganization",
                "name": "Ciqura Labs",
                "sameAs": "https://ciquralabs.ciqur24.com",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "113, First Floor, Shagun Arcade, Vijay Nagar",
                  "addressLocality": "Indore",
                  "addressRegion": "MP",
                  "postalCode": "452010",
                  "addressCountry": "IN"
                }
              },
              "educationalCredentialAwarded": "Professional Industry Certification & Ciqur24 Internship Letter",
              "occupationalCategory": "IT and Cybersecurity Professional",
              "hasCourseInstance": {
                "@type": "CourseInstance",
                "courseMode": "Offline/Online Hybrid",
                "location": "Indore, India"
              }
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": `Where is the ${program.title} training located in Indore?`,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Our training center is located at 113, First Floor, Shagun Arcade, Vijay Nagar, Indore. We offer both offline and live online sessions."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does this course include an internship?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, eligible students in our professional tracks receive a guaranteed internship opportunity at our headquarters, Ciqur24 Bengaluru."
                  }
                }
              ]
            }
          ])}
        </script>
      </Helmet>

      {/* ================= HERO SECTION ================= */}
      <section style={{ position: 'relative', height: '480px', overflow: 'hidden' }}>
        <img
          src={program.image}
          alt={`${program.title} professional training at Ciqura Labs - Vijay Nagar, Indore IT hub`}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          loading="eager" 
          fetchpriority="high"
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.95))' }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', padding: '0 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
            <Button className="btn-secondary" onClick={() => navigate('/programs')} style={{ marginBottom: '24px', backdropFilter: 'blur(8px)' }}>
              <ArrowLeft size={18} /> Back to Programs
            </Button>

            <h1 className="display-huge" style={{ marginBottom: '16px' }}>
              {program.title} <span style={{ color: 'var(--brand-primary)' }}>Expertise</span>
            </h1>

            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', marginBottom: '24px' }}>
              {program.description}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Clock size={20} style={{ color: 'var(--brand-primary)' }} />
                <span className="body-medium" style={{ fontWeight: 600 }}>{program.duration} Track</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Award size={20} style={{ color: 'var(--brand-primary)' }} />
                <span className="body-medium" style={{ fontWeight: 600 }}>Global Certification Track</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CIQUR24 CREDIBILITY BLOCK ============ */}
      <section style={{ padding: '90px 7.6923%', background: 'linear-gradient(180deg, rgba(0,255,209,0.05), transparent)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="display-medium" style={{ marginBottom: '16px' }}>Industry-Ready Training from Ciqur24</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', margin: '0 auto' }}>
              Ciqura Labs is the dedicated training division of <strong style={{ color: 'var(--brand-primary)' }}>Ciqur24 LLP</strong>. We bridge the gap between academic theory and enterprise-level cybersecurity operations.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px' }}>
            <CredCard icon={<ShieldCheck size={36} />} title="Live SOC Environment" text="Train in our virtualized Security Operations Center using real-world threat datasets." />
            <CredCard icon={<Briefcase size={36} />} title="Bengaluru Internship" text="Top performers gain direct access to internship roles at our Bengaluru HQ delivery center." />
            <CredCard icon={<Users size={36} />} title="Indore Campus Hub" text="Located in the heart of Vijay Nagar, offering the best networking and peer learning environment." />
          </div>
        </div>
      </section>

      {/* ================= MAIN CONTENT SECTION ================= */}
      <section style={{ padding: '80px 7.6923%' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr', gap: '60px' }} className="responsive-grid">
            
            {/* LEFT SIDE: Educational Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '60px' }}>
              <Section title="Comprehensive Syllabus" icon={<BookOpen size={28} />}>
                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: '12px', overflow: 'hidden' }}>
                  <Accordion type="single" collapsible>
                    {program.curriculum.map((mod, i) => (
                      <AccordionItem key={i} value={`item-${i}`} style={{ borderBottom: '1px solid var(--border-subtle)', padding: '0 24px' }}>
                        <AccordionTrigger className="hover:no-underline">
                          <span className="heading-3">{i + 1}. {mod.module}</span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul style={{ padding: '10px 0 20px 20px', listStyleType: 'square' }}>
                            {mod.topics.map((t, ti) => (
                              <li key={ti} className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>{t}</li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </Section>

              <Section title="Tools & Tech Stack" icon={<Wrench size={28} />}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {program.tools.map((tool, i) => (
                    <span key={i} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--brand-primary)', padding: '12px 24px', fontWeight: 600, borderRadius: '4px' }}>
                      {tool}
                    </span>
                  ))}
                </div>
              </Section>
            </div>

            {/* RIGHT SIDE: Conversion Sidebar */}
            <div style={{ position: 'sticky', top: '100px', height: 'fit-content' }}>
              <div style={{ background: 'var(--bg-secondary)', border: '2px solid var(--brand-primary)', padding: '40px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'inline-block', background: 'var(--brand-primary)', color: '#000', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 800, marginBottom: '16px' }}>
                  ADMISSIONS OPEN 2026
                </div>
                <h3 className="display-small" style={{ marginBottom: '16px' }}>Start Your Journey</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                  Limited seats available for our offline batch in Vijay Nagar, Indore.
                </p>

                <ul style={{ textAlign: 'left', marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {program.skills.slice(0, 5).map((skill, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '15px' }}>
                      <CheckCircle size={18} style={{ color: 'var(--brand-primary)' }} /> {skill}
                    </li>
                  ))}
                </ul>

                <Button
                  className="btn-primary"
                  onClick={() => window.open(registrationLink, '_blank', 'noopener,noreferrer')}
                  style={{ width: '100%', height: '60px', fontSize: '18px', fontWeight: 700 }}
                >
                  Apply for Admission <ExternalLink size={20} style={{ marginLeft: '10px' }} />
                </Button>
                
                <p style={{ marginTop: '20px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  Call our Indore Office: <a href="tel:+919303998387" style={{ color: 'var(--brand-primary)', textDecoration: 'none', fontWeight: 'bold' }}>+91 9303998387</a>
                </p>
              </div>

              {/* Career Paths Card */}
              <div style={{ marginTop: '30px', padding: '30px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <TrendingUp size={24} style={{ color: 'var(--brand-primary)' }} />
                  <h4 className="heading-3">Target Roles</h4>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {program.careers.map((career, i) => (
                    <span key={i} style={{ background: 'var(--bg-primary)', padding: '6px 12px', fontSize: '13px', border: '1px solid var(--border-subtle)', borderRadius: '4px' }}>{career}</span>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

/* ================== HELPER COMPONENTS ================== */

const CredCard = ({ icon, title, text }) => (
  <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', padding: '40px', borderRadius: '12px', transition: 'transform 0.3s ease' }} className="hover:scale-105">
    <div style={{ color: 'var(--brand-primary)', marginBottom: '20px' }}>{icon}</div>
    <h3 className="heading-3" style={{ marginBottom: '12px' }}>{title}</h3>
    <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>{text}</p>
  </div>
);

const Section = ({ title, icon, children }) => (
  <div style={{ marginBottom: '60px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
      <div style={{ background: 'var(--bg-secondary)', padding: '12px', borderRadius: '8px', color: 'var(--brand-primary)' }}>{icon}</div>
      <h2 className="display-medium">{title}</h2>
    </div>
    {children}
  </div>
);

export default ProgramDetailPage;